import { FC } from 'react';

import { useAtomValue } from 'jotai';

import { Prototype } from '@elva-visualizer';
import { cameraSetupMapAtom, comparisonCameraSetupMapAtom, showVisualizationsAtom, useEnsureAtomValue } from '@store';
import { colors } from '@ui';

import { CameraFromCalibration } from '../3d/CameraFromCalibration';
import { OpenCVDistortionPass } from '../3d/OpenCVDistortionPass';

export type ThreeDFlags = Partial<Record<'grid' | 'background' | 'lensDistortion' | 'club' | 'skeleton', boolean>>;

type ViewProps = { origin: string; view: number };

type CameraProps = ViewProps & Pick<ThreeDFlags, 'lensDistortion'>;

const CURRENT = 'current';

const Camera = ({ view, origin, lensDistortion }: CameraProps) => {
    const cameraMap = useEnsureAtomValue(origin === CURRENT ? cameraSetupMapAtom : comparisonCameraSetupMapAtom);
    const cameraConfig = cameraMap?.[view];

    if (!cameraConfig) return null;
    return (
        <>
            <CameraFromCalibration config={cameraConfig} />
            {lensDistortion && <OpenCVDistortionPass config={cameraConfig} />}
        </>
    );
};

export const ThreeDPlayer: FC<ViewProps & ThreeDFlags> = ({
    origin,
    view,
    grid,
    skeleton,
    background,
    lensDistortion,
}) => {
    const cameraMap = useEnsureAtomValue(cameraSetupMapAtom);
    const showVisualizations = useAtomValue(showVisualizationsAtom);

    const cameraConfig = cameraMap?.[view];

    if (!cameraConfig) return null;

    return (
        <Prototype.MemoCanvasScene background={background}>
            <Camera origin={origin} view={view} lensDistortion={lensDistortion} />
            {skeleton && <Prototype.Skeleton origin={origin} />}
            {skeleton && <Prototype.Club origin={origin} />}
            {skeleton && <Prototype.GolfBall origin={origin} />}
            {showVisualizations && <Prototype.KeypointPath origin={origin} />}
            {showVisualizations && <Prototype.Visualizations origin={origin} />}
            {showVisualizations && <Prototype.SwingPlanes swing={origin === CURRENT ? 'active' : 'comparison'} />}
            {showVisualizations && <Prototype.Paths origin={origin} />}
            {grid && (
                <Prototype.Grid
                    rotation={[Math.PI / 2, 0, 0]}
                    infiniteGrid
                    fadeFrom={0}
                    fadeStrength={2}
                    fadeDistance={30}
                    cellThickness={1}
                    cellSize={0.25}
                    cellColor={colors.bluegray[200]}
                    sectionColor={colors.bluegray[200]}
                />
            )}
        </Prototype.MemoCanvasScene>
    );
};
